/**
 * The look, in one place: toon materials with a code-generated gradient ramp.
 *
 * The stylized *Hello Neighbor*-ish feel comes from three things — saturated palette
 * colors (from the profile), a banded light ramp instead of smooth Lambert falloff, and
 * soft grounding shadows. The ramp is a handful of bytes as a `DataTexture` built at
 * runtime: zero assets, zero bundle cost.
 *
 * Architecture shares one material instance per color (cheap state changes, many
 * meshes); interactive objects that need per-instance emissive focus feedback create
 * their own via JSX and only borrow the ramp.
 */

import {
  CanvasTexture,
  Color,
  DataTexture,
  MeshToonMaterial,
  NearestFilter,
  RepeatWrapping,
  RGBAFormat,
  SRGBColorSpace,
} from 'three';
import type { PathSegment, Rect } from '../game/profile/types';
import { groundsCanvas, surfaceCanvas, type SurfacePattern } from './textures';

let gradientMap: DataTexture | null = null;

/**
 * The light ramp: banded, not smooth — nearest filtering keeps each step crisp, which is
 * what makes the look stylized rather than photographic.
 *
 * Five steps rather than the original three, and the darkest one lifted well off black.
 * A three-step ramp put a hard line across every wall and let unlit faces fall to a flat
 * dark that no amount of fill could soften, because two bands is all the range there was.
 * Real rooms are not like that: light bounces off the floor and the walls and fills the
 * shadow side. There is no global illumination here to do that — the ramp's floor and the
 * hemisphere fill in `GameCanvas` stand in for it, and they have to be read together.
 */
export function getGradientMap(): DataTexture {
  if (gradientMap) return gradientMap;

  const steps = new Uint8Array([
    ...[168, 168, 168, 255],
    ...[196, 196, 196, 255],
    ...[219, 219, 219, 255],
    ...[238, 238, 238, 255],
    ...[255, 255, 255, 255],
  ]);
  gradientMap = new DataTexture(steps, 5, 1, RGBAFormat);
  gradientMap.minFilter = NearestFilter;
  gradientMap.magFilter = NearestFilter;
  gradientMap.needsUpdate = true;
  return gradientMap;
}

const shared = new Map<string, MeshToonMaterial>();

/** One material per color, shared by every mesh painted with it. Do not mutate. */
export function toonMaterial(color: string): MeshToonMaterial {
  const existing = shared.get(color);
  if (existing) return existing;

  const material = new MeshToonMaterial({
    color: new Color(color),
    gradientMap: getGradientMap(),
  });
  shared.set(color, material);
  return material;
}

const surfaces = new Map<string, MeshToonMaterial>();

/**
 * A toon material carrying a procedural pattern (wallpaper, planks, tile…), tiled in
 * world units — `repeatX`/`repeatY` say how many pattern tiles the surface spans, so
 * a long wall and a short one get the same stripe width. Repeats are part of the
 * cache key; a house produces a couple of dozen of these at most. Skirting, cornice and
 * casings are not drawn here — they are real geometry now (see `buildTrim`).
 *
 * Falls back to the plain colored material where a 2D canvas is unavailable.
 */
/**
 * The lawn's material: one non-repeating image over the whole grounds, because the mud
 * path in it follows the maze's actual corridors and a repeat would scatter it. Cached
 * like the surfaces above; a build has exactly one grounds, so the colour is key enough.
 */
/**
 * Cached differently from the surfaces above, and it has to be. A wall pattern is a tile:
 * two levels painted the same colour want the same one. The grass is not — the mud path in
 * it follows *this* maze's corridors, so the key carries the maze, and a level swap draws
 * a new one. Exactly one grounds is in the world at a time, so the previous one is
 * released here rather than left in a map that only grows: a full-lawn texture is the
 * largest single image the game makes, and three of them is not a cache, it is the leak.
 */
let grounds: { key: string; material: MeshToonMaterial } | null = null;

export function groundsMaterial(
  color: string,
  rect: Rect,
  paths: readonly PathSegment[],
): MeshToonMaterial {
  const key = [
    color,
    rect.x,
    rect.z,
    rect.width,
    rect.depth,
    paths.length,
    ...paths.flatMap((path) => [path.from.x, path.from.z, path.to.x, path.to.z]),
  ].join(':');
  if (grounds?.key === key) return grounds.material;

  const canvas = groundsCanvas(color, rect, paths);
  if (!canvas) return toonMaterial(color);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;

  const material = new MeshToonMaterial({ map: texture, gradientMap: getGradientMap() });
  // The ramp is shared and stays; the map was made here and goes with the material.
  grounds?.material.map?.dispose();
  grounds?.material.dispose();
  grounds = { key, material };
  return material;
}

export function surfaceMaterial(
  pattern: SurfacePattern,
  color: string,
  repeatX: number,
  repeatY: number,
): MeshToonMaterial {
  const key = `${pattern}:${color}:${repeatX.toFixed(2)}x${repeatY.toFixed(2)}`;
  const existing = surfaces.get(key);
  if (existing) return existing;

  const canvas = surfaceCanvas(pattern, color);
  if (!canvas) return toonMaterial(color);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.colorSpace = SRGBColorSpace;

  // The texture carries the palette color already; a white base leaves it unmultiplied.
  const material = new MeshToonMaterial({ map: texture, gradientMap: getGradientMap() });
  surfaces.set(key, material);
  return material;
}
